import React, { useState, useEffect } from 'react';
import { getTermineByWeek, getUsersByOrganization, updateTermin } from '../../api';

const MultiAssignModal = ({ user, onClose, organizations, teams, currentDate }) => {
    const [step, setStep] = useState(1); // 1: Week Selection, 2: Appointment List
    const [selectedDate, setSelectedDate] = useState(null);
    const [weekDates, setWeekDates] = useState([]);
    const [appointments, setAppointments] = useState([]);
    const [consultants, setConsultants] = useState([]);
    const [selectedConsultant, setSelectedConsultant] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [selectedAppointments, setSelectedAppointments] = useState([]);

    // Initialize week dates based on currentDate from parent component
    useEffect(() => {
        const dates = [];
        // Use currentDate from parent if provided, otherwise use today
        const baseDate = currentDate ? new Date(currentDate) : new Date();
        const day = baseDate.getDay(); // 0 = Sunday, 1 = Monday, ...
        const diff = baseDate.getDate() - day + (day === 0 ? -6 : 1); // Adjust to Monday
        const monday = new Date(baseDate);
        monday.setDate(diff);

        for (let i = 0; i < 7; i++) {
            const date = new Date(monday);
            date.setDate(monday.getDate() + i);
            dates.push(date);
        }
        setWeekDates(dates);

        // Load consultants initially
        loadConsultants();
    }, [currentDate]);

    const loadConsultants = async () => {
        try {
            // Assuming role_type 6 is Fachberater
            const response = await getUsersByOrganization(user.id, user.organization_id);
            if (response.success) {
                const filtered = response.users.filter(u => u.role_type === 6);
                setConsultants(filtered);
            }
        } catch (err) {
            console.error("Error loading consultants:", err);
        }
    };

    const handleDateSelect = async (date) => {
        setSelectedDate(date);
        setStep(2);
        await loadAppointments(date);
    };

    const loadAppointments = async (date) => {
        setLoading(true);
        setError('');
        try {
            // Calculate week start for the API
            const d = new Date(date);
            const day = d.getDay();
            const diff = d.getDate() - day + (day === 0 ? -6 : 1);
            const weekStart = new Date(d.setDate(diff)).toISOString().split('T')[0];

            const response = await getTermineByWeek(user.id, weekStart, {
                organizationId: user.organization_id
            });

            if (response.success) {
                // Filter for the specific selected date
                const dateStr = date.toISOString().split('T')[0];
                const dayAppointments = response.termine.filter(t => t.datum === dateStr);
                setAppointments(dayAppointments);
            } else {
                setError('Fehler beim Laden der Termine');
            }
        } catch (err) {
            setError('Netzwerkfehler: ' + err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleAssign = async () => {
        if (!selectedConsultant || selectedAppointments.length === 0) return;

        setLoading(true);
        try {
            const consultant = consultants.find(c => c.id.toString() === selectedConsultant);

            const promises = selectedAppointments.map(terminId =>
                updateTermin(user.id, terminId, {
                    mitarbeiter_id: consultant.id,
                    mitarbeiter_name: consultant.full_name || consultant.username,// Fallback
                    mitarbeiter_team_id: consultant.team_id
                })
            );

            await Promise.all(promises);

            // Refresh list
            await loadAppointments(selectedDate);
            setSelectedAppointments([]); // Clear selection

        } catch (err) {
            setError('Fehler beim Zuweisen: ' + err.message);
        } finally {
            setLoading(false);
        }
    };

    const toggleAppointmentSelection = (terminId) => {
        setSelectedAppointments(prev =>
            prev.includes(terminId)
                ? prev.filter(id => id !== terminId)
                : [...prev, terminId]
        );
    };

    const formatDate = (date) => {
        return date.toLocaleDateString('de-DE', { weekday: 'long', day: '2-digit', month: '2-digit' });
    };

    const generateColor = (str) => {
        if (!str) return '#ccc';
        let hash = 0;
        for (let i = 0; i < str.length; i++) {
            hash = str.charCodeAt(i) + ((hash << 5) - hash);
        }
        const c = (hash & 0x00FFFFFF).toString(16).toUpperCase();
        return '#' + '00000'.substring(0, 6 - c.length) + c;
    };

    return (
        <div className="modal-overlay">
            <div className="modal-content multi-assign-modal">
                <div className="modal-header">
                    <h3 style={{ width: '80%' }}>Termine zuweisen</h3>
                    <button className="close-button" onClick={onClose}>&times;</button>
                </div>

                <div className="modal-body">
                    {step === 1 && (
                        <div className="week-selection">
                            <h4>Wählen Sie einen Tag aus:</h4>
                            <div className="date-grid">
                                {weekDates.map((date, index) => (
                                    <button
                                        key={index}
                                        className="date-button"
                                        onClick={() => handleDateSelect(date)}
                                    >
                                        {formatDate(date)}
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}

                    {step === 2 && (
                        <div className="appointment-list-view">
                            <div className="list-header">
                                <button className="back-button" onClick={() => setStep(1)}>← Zurück</button>
                                <h4>Termine am {formatDate(selectedDate)}</h4>
                            </div>

                            <div className="controls-row">
                                <select
                                    value={selectedConsultant}
                                    onChange={(e) => setSelectedConsultant(e.target.value)}
                                    className="consultant-select"
                                >
                                    <option value="">Fachberater auswählen...</option>
                                    {consultants.map(c => (
                                        <option key={c.id} value={c.id}>{c.full_name || c.username}</option>
                                    ))}
                                </select>

                                <button
                                    className="assign-button"
                                    disabled={!selectedConsultant || selectedAppointments.length === 0 || loading}
                                    onClick={handleAssign}
                                >
                                    {loading ? '...' : 'Zuweisen'}
                                </button>
                            </div>

                            {error && <div className="error-message">{error}</div>}

                            <div className="appointments-table-container">
                                <table className="appointments-table">
                                    <thead>
                                        <tr>
                                            <th style={{ width: '40px' }}>
                                                <input
                                                    type="checkbox"
                                                    onChange={(e) => {
                                                        if (e.target.checked) {
                                                            setSelectedAppointments(appointments.map(t => t.id));
                                                        } else {
                                                            setSelectedAppointments([]);
                                                        }
                                                    }}
                                                    checked={appointments.length > 0 && selectedAppointments.length === appointments.length}
                                                />
                                            </th>
                                            <th>Kunde</th>
                                            <th>Uhrzeit</th>
                                            <th>Projekt</th>
                                            <th>Aktueller Fachberater</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {appointments.length === 0 ? (
                                            <tr><td colSpan="5" className="no-data">Keine Termine für diesen Tag</td></tr>
                                        ) : (
                                            appointments.map(termin => {
                                                const rowColor = termin.mitarbeiter_name ? generateColor(termin.mitarbeiter_name) : null;
                                                return (
                                                    <tr
                                                        key={termin.id}
                                                        style={rowColor ? {
                                                            backgroundColor: rowColor + '20', // 12% opacity
                                                            borderLeft: `4px solid ${rowColor}`
                                                        } : {
                                                            borderLeft: '4px solid transparent'
                                                        }}
                                                    >
                                                        <td>
                                                            <input
                                                                type="checkbox"
                                                                checked={selectedAppointments.includes(termin.id)}
                                                                onChange={() => toggleAppointmentSelection(termin.id)}
                                                            />
                                                        </td>
                                                        <td>
                                                            {termin.kunde_name ||
                                                                termin.customer_name ||
                                                                (termin.kunde_vorname && termin.kunde_nachname ? `${termin.kunde_vorname} ${termin.kunde_nachname}` :
                                                                    termin.kunde_vorname ? termin.kunde_vorname :
                                                                        termin.kunde_nachname ? termin.kunde_nachname :
                                                                            'Unbekannter Kunde')}
                                                        </td>
                                                        <td>{termin.uhrzeit}</td>
                                                        <td>{termin.team_name || '-'}</td>
                                                        <td>{termin.mitarbeiter_name || '-'}</td>
                                                    </tr>
                                                );
                                            })
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            <style jsx>{`
        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background-color: rgba(0, 0, 0, 0.5);
          display: flex;
          justify-content: center;
          align-items: center;
          z-index: 1000;
        }

        .multi-assign-modal {
          background: white;
          border-radius: 8px;
          width: 90%;
          max-width: 800px;
          max-height: 90vh;
          display: flex;
          flex-direction: column;
          box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
        }

        .modal-header {
          width: 100%;
          padding: 15px 20px;
          border-bottom: 1px solid #e2e8f0;
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .modal-body {
          padding: 20px;
          overflow-y: auto;
          flex: 1;
        }

        .close-button {
          background: none;
          width: 15%;
          border: none;
          font-size: 1.5rem;
          cursor: pointer;
          color: #718096;
        }

        .date-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
          gap: 15px;
          margin-top: 20px;
        }

        .date-button {
          padding: 10px;
          background: #1387d4ff;
          border: 1px solid #e2e8f0;
          border-radius: 8px;
          cursor: pointer;
          transition: all 0.2s;
          font-weight: 500;
          color: #ffffffff;
        }

        .date-button:hover {
          background: #0f6cb0;
          border-color: #0f6cb0;
          color: white;
          transform: translateY(-1px);
        }

        .list-header {
          display: flex;
          align-items: center;
          gap: 15px;
          margin-bottom: 20px;
        }

        .back-button {
          background: none;
          border: none;
          color: #4a5568;
          cursor: pointer;
          font-size: 0.9rem;
        }

        .controls-row {
          display: flex;
          gap: 15px;
          margin-bottom: 20px;
          padding: 15px;
          background: #f7fafc;
          border-radius: 8px;
        }

        .consultant-select {
          flex: 1;
          padding: 8px 12px;
          border: 1px solid #e2e8f0;
          border-radius: 4px;
        }

        .assign-button {
          padding: 8px 20px;
          background: #48bb78;
          color: white;
          border: none;
          border-radius: 4px;
          cursor: pointer;
          font-weight: 500;
        }

        .assign-button:disabled {
          background: #cbd5e0;
          cursor: not-allowed;
        }

        .appointments-table {
          width: 100%;
          border-collapse: collapse;
        }

        .appointments-table th,
        .appointments-table td {
          padding: 12px;
          text-align: left;
          border-bottom: 1px solid #e2e8f0;
        }

        .appointments-table th {
          background: #f7fafc;
          font-weight: 600;
          color: #4a5568;
        }

        .no-data {
          text-align: center;
          color: #718096;
          padding: 30px;
        }
      `}</style>
        </div>
    );
};

export default MultiAssignModal;
